<template>
	<div class="xs-12 wrap">
		<!-- First step -->
		<div v-if="step === 'first' && !isLoading">
			<!-- Entities Switcher -->
			<div class="xs-12">
				<div class="flex xs-12 bb1 bc7">
					<div v-for="(tab, i) in createOptions" :key="i">
						<div
							@click="entityType = tab.value"
							class="cu2 c1 fw7 p4 bb2 bc0"
							:class="{ 'bc2 c2': tab.value === entityType }"
						>
							{{ tab.name }}
						</div>
					</div>
				</div>
			</div>

			<!-- Select an existing entity -->
			<div class="p4 pb15">
				<div class="xs-12 fs4 fw7 lh125 p1 mb1">
					{{ $t("choose_an_existing") }} {{ entityType }}
				</div>
				<select-entity
					v-model="entityId"
					:entityType="entityType"
					:placeholder="'Search ' + entityType"
					@input="fetchEntity()"
				/>
				<!-- Create a new empty entity -->
				<div class="flex xs-12 xs-right xs-middle mt4 mb10">
					<div class="mr4">{{ $t("cannot_find") }} {{ entityType }} ?</div>
					<j-button :isLoading="isLoading" size="small" @click="createEntity()"
						>{{ $t("create_new") }} {{ entityType }}</j-button
					>
				</div>
			</div>
		</div>

		<!-- Loading step -->
		<j-spinner size="small" class="mt8 mb8" v-if="isLoading" />

		<!-- Second step -->
		<div class="p4">
			<core-entity-card
				v-if="step === 'second' && !isLoading"
				mode="inline"
				:pinnable="false"
				:hasActions="isCreate"
				:entity="entity"
				@updated="fetchEntity()"
				@deleted="fetchEntity()"
				@added="fetchEntity()"
			/>
		</div>
	</div>
</template>

<script>
import JButton from "@/components/JButton"
import JSpinner from "@/components/JSpinner"
import JBaseSelect from "@/components/JBaseSelect"
import SelectEntity from "./SelectEntity"
import CoreEntityCard from "@/modules/core/components/CoreEntityCard/"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	name: "AddNewEntity",
	components: {
		JButton,
		JBaseSelect,
		SelectEntity,
		CoreEntityCard,
		JSpinner
	},
	props: {
		value: {
			required: false,
			activeEndorsement: {}
		},
		createOptions: {
			required: false,
			default: () => {
				return [
					{ name: "Individual", value: "individual" },
					{ name: "Company", value: "company" },
					{ name: "Object", value: "object" }
				]
			}
		}
	},
	data() {
		return {
			isLoading: false,
			isCreate: false,
			step: "first",
			entityType: "individual",
			entity: "",
			entityId: ""
		}
	},

	methods: {
		emitValue() {
			this.$emit("input", {
				...this.entity,
				entityType: this.entityType
			})
		},

		/**
		 * Create entity with all fields based on schema
		 */
		async createEntity() {
			this.isCreate = true
			this.isLoading = true
			// Define keys for universal queries
			let apiKeys
			if (this.entityType === "individual")
				apiKeys = { q: "createIndividual", w: "createIndividualInput" }
			if (this.entityType === "company")
				apiKeys = { q: "createCompany", w: "createCompanyInput" }
			if (this.entityType === "object")
				apiKeys = { q: "createObject", w: "createObjectInput" }
			if (this.entityType === "organization")
				apiKeys = { q: "createOrganization", w: "createOrganizationInput" }
			if (this.entityType === "participant")
				apiKeys = { q: "createInternal", w: "createInternalInput" }

			// Query
			const query = `
				mutation ${apiKeys.q}($input: ${apiKeys.w}!) {
					${apiKeys.q}(${apiKeys.w}: $input) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
      `
			const variables = {
				input: {}
			}

			// Create individual with all fields based on schema
			const res = await api({ query, variables })

			this.isLoading = false

			// Error
			if (!res?.data?.[apiKeys.q]?.createdStatus?.id) {
				console.log("Error when creating the entity")
				return
			} else {
				this.entityId = res.data[apiKeys.q].createdStatus.id
				this.fetchEntity()
			}
		},

		async fetchEntity() {
			// Individual query
			const individualQuery = gql`
				query individuals($where: individualWhereInput) {
					individuals(where: $where) {
						list {
							__typename
							id
							name
							englishFirstName
							englishLastName
							chineseFirstName
							chineseLastName
							dateOfBirth
							gender
							occupation
							maritalStatus
							employmentStatus
							salutation
							preferredCommunicationChannel
							preferredLanguage
							countryOfResidency
							incomeRange {
								exclusiveMaximum
								exclusiveMinimum
								minimum
								maximum
							}
							acceptsMarketing
							source
							internalCode
							contacts {
								id
								type
								value
							}
							identities {
								id
								type
								value
							}
							addresses {
								id
								fields
								fields2 {
									type
									value
								}
								type
							}
							facts {
								id
								type
								value {
									booleanValue
									dateValue
									numberValue
									stringValue
									arrayValue {
										stringValue
										booleanValue
										dateValue
										numberValue
										objectValue {
											key
											value {
												stringValue
												booleanValue
												dateValue
												numberValue
											}
										}
										arrayValue {
											stringValue
											booleanValue
											dateValue
											numberValue
											arrayValue {
												stringValue
												booleanValue
												dateValue
												numberValue
											}
										}
									}
								}
							}
							relationships {
								type
								id
								entity {
									id
									name
								}
							}
						}
					}
				}
			`
			const companyQuery = gql`
				query companies($where: companyWhereInput) {
					companies(where: $where) {
						list {
							__typename
							id
							name
							registrationNumber
							natureOfBusiness
							acceptsMarketing
							photoPath
							source
							contacts {
								id
								type
								value
							}
							addresses {
								id
								fields
								type
								fields2 {
									type
									value
								}
							}
							identities {
								id
								type
								value
							}
							facts {
								id
								type
								value {
									booleanValue
									dateValue
									numberValue
									stringValue
									arrayValue {
										stringValue
										booleanValue
										dateValue
										numberValue
										objectValue {
											key
											value {
												stringValue
												booleanValue
												dateValue
												numberValue
											}
										}
										arrayValue {
											stringValue
											booleanValue
											dateValue
											numberValue
											arrayValue {
												stringValue
												booleanValue
												dateValue
												numberValue
											}
										}
									}
								}
							}
							relationships {
								type
								id
								entity {
									id
									name
								}
							}
						}
					}
				}
			`
			const objectQuery = gql`
				query objects($where: objectWhereInput) {
					objects(where: $where) {
						list {
							__typename
							id
							name
							internalCode
							photoPath
							source
							typeId
							contacts {
								id
								type
								value
							}
							addresses {
								id
								fields
								type
								fields2 {
									type
									value
								}
							}
							identities {
								id
								type
								value
							}
							facts {
								id
								type
								value {
									booleanValue
									dateValue
									numberValue
									stringValue
									arrayValue {
										stringValue
										booleanValue
										dateValue
										numberValue
										objectValue {
											key
											value {
												stringValue
												booleanValue
												dateValue
												numberValue
											}
										}
										arrayValue {
											stringValue
											booleanValue
											dateValue
											numberValue
											arrayValue {
												stringValue
												booleanValue
												dateValue
												numberValue
											}
										}
									}
								}
							}
							relationships {
								type
								id
								entity {
									id
									name
								}
							}
						}
					}
				}
			`
			// Based on this.entityType decide proper naming in the query
			let apiKeys
			if (this.entityType === "individual")
				apiKeys = { queryType: "individuals", query: individualQuery }
			if (this.entityType === "company")
				apiKeys = { queryType: "companies", query: companyQuery }
			if (this.entityType === "object")
				apiKeys = { queryType: "objects", query: objectQuery }

			this.isLoading = true
			const res = await api({
				query: apiKeys.query,
				variables: { where: { id: this.entityId } }
			})
			this.isLoading = false

			// Save
			this.entity = res?.data?.[apiKeys.queryType]?.list?.[0]
			if (!this.entity) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: "Error when trying to fetch entity."
					}
				})
			} else {
				this.step = "second"
				this.$emit("showButtons")
				this.emitValue()
			}
		}
	}
}
</script>
